import React, { useRef, useState, useCallback, useEffect } from 'react'

import {
    Box,
    Button,
    Tooltip,
    Link
} from '@mui/material';

import { useNavigate } from 'react-router-dom';

// import DateFilter from '@inovua/reactdatagrid-community/DateFilter'
import { icons } from '../../common/CommonIcons';
import CommonBreadcumbs from '../../common/CommonBreadcumbs';
import CommonRestricted from '../../common/CommonRestricted';
import DataGridToolbar from '../../common/DataGridToolbar';
import DataGridTable from '../../common/DataGridTable';
// import DataGridRowActionMenu from '../../common/DataGridRowActionMenu';
import { useAppContextFeatures } from '../../../context/AppContext';

import { useDataGridParams } from '../../../hooks/useDataGridParams';
import { useDocuments, documentTypes, targetTypes } from '../../../hooks/api/useDocuments';
import { useSystemPrompt } from '../../../hooks/useSystemPrompt';
import { useSystemRoutes, routeTypes } from '../../../hooks/useSystemRoutes';
import { useSystemHead, headTitles } from '../../../hooks/useSystemHead';
import { usePermissions, scopeIds, scopeTypes, functionTypes } from '../../../hooks/usePermissions';
import { useDependencies, dependencyModules, targetTypes as dependencyTargetTypes } from '../../../hooks/api/useDependencies';

import { useTranslations } from '../../../hooks/useTranslations';
import { useAuthAccess } from '../../../hooks/useAuthAccess';

export default function ListTransferRequisitions({
    routeType
}) {
    const { t } = useTranslations({ ns: ["common", "@document"] })

    const navigate = useNavigate();
    const { promptMessage } = useSystemPrompt();
    const { list, remove } = useDocuments({ documentType: documentTypes.transferRequisition });
    const { RenderDatagridDisplay: RenderDependencyDatagridDisplay } = useDependencies()

    const { defineColumns, columns, filterValues, getRenderColumnLink, getRenderColumnTextDisplay } = useDataGridParams('id')
    const [selectedIds, setSelectedIds] = useState([])
    const [refreshDataGrid, setRefreshDataGrid] = useState(0)
    const { getLocationIds } = useAppContextFeatures();

    const gridRef = React.createRef();
    const { getRoute } = useSystemRoutes(routeType);
    const { getHtmlHead } = useSystemHead(routeType)
    const { isPermissionAllow } = usePermissions(routeType, scopeIds.document.id, scopeTypes.document.transferRequisition.id)

    const { getAvailableLocations } = useAuthAccess()

    const resetDataGridTableSelection = () => {
        setSelectedIds([])
    }

    useEffect(() => {

        setRefreshDataGrid(val => val + 1)

    }, [getLocationIds])

    const fetchData = async (props) => {

        // console.log('getAvailableLocations', getAvailableLocations())

        resetDataGridTableSelection();

        try {
            props.dataTypes = ["dependency"]

            props.customFilterLocationEnable = true;
            props.customFilterLocationFields = ['location_id', 'to_location_id']

            const result = await list(props)

            let rawData = result?.data?.documents

            let newData = []

            rawData.forEach(i => {

                let _i = i

                let outward = _i?.outward_dependencies
                let inward = _i?.inward_dependencies

                let all = [...outward, ...inward]

                // declare a value and allocate the combined data
                _i.all_dependencies = all

                newData.push(_i)
            })

            // need to check the data is allow verify in or out
            const allowLocationIds = getAvailableLocations().map(i => i.id)

            newData.forEach((e1, index) => {

                let allowVerifyIn = false
                let allowVerifyOut = false

                // check if verify in allow
                if (allowLocationIds.includes(e1.location_id)) {
                    allowVerifyOut = true;
                }

                // check if verify out allow
                if (allowLocationIds.includes(e1.to_location_id)) {
                    allowVerifyIn = true
                }

                // dataVerify.push(e1)
                newData[index].allowVerifyIn = allowVerifyIn
                newData[index].allowVerifyOut = allowVerifyOut
            })

            return {
                data: newData,
                count: result?.data?.total
            }

        } catch (error) {

            promptMessage(false, `Error getting results (${error?.message})`)

            return {
                data: [],
                count: 0
            }
        }

    }

    useEffect(() => {

        defineColumnsView()

    }, [])

    const defineColumnsView = () => {
        defineColumns([
            {
                name: 'location_name', header: t('location')
            },
            {
                name: 'to_location_name', header: t('destination')
            },
            {
                name: 'doc_number',
                header: t('docNo'),
                renderValue: ({ value, cellProps }) => {

                    if (isPermissionAllow({ functionType: functionTypes.pageView.id }) === false) {
                        return getRenderColumnTextDisplay(value)
                    }

                    let path = getRoute(`documents/transferRequisition/view/${cellProps.data.id}`);

                    return getRenderColumnLink(path, value)

                }
            },
            {
                name: 'doc_date', header: t('date')
            },
            {
                name: 'all_dependencies',
                header: t('relatedDocs'),
                renderValue: ({ value, cellProps }) => {

                    return (
                          <RenderDependencyDatagridDisplay
                                id={cellProps.data.id}
                                values={value}
                                renderValue={({ id, otherUrl }) => {

                                    if (otherUrl) {
                                        return getRoute(otherUrl)
                                    }

                                    return getRoute(`documents/transferRequisition/view/${id}`)
                                }}
                          />
                    )

                }

            },
        ])
    }

    const handleDelete = async () => {

        try {

            let result = await remove({ ids: selectedIds, targetType: targetTypes.transferRequisition })

            promptMessage(true, result?.message)
            setRefreshDataGrid(val => val + 1)

        } catch (error) {
            promptMessage(false, error?.message)
        }

    }

    const RenderToolbar = () => {

        const getToolbarMainMenus = () => {

            let props = [];

            if (isPermissionAllow({ functionType: functionTypes.pageNew.id })) {
                props.push({
                    displayIconName: icons.plus,
                    onClick: () => {

                        navigate(getRoute("documents/transferRequisition/new"));

                    }
                })
            }

            return props
        }

        const getActionOnClick = () => {

            let handleFunction = null

            if (isPermissionAllow({ functionType: functionTypes.remove.id })) {
                handleFunction = handleDelete
            }

            return handleFunction
        }

        return (
              <DataGridToolbar
                    selectedRowCount={
                        selectedIds.length
                    }
                    mainMenus={
                        getToolbarMainMenus()
                    }
                    actionOnClickRemove={
                        getActionOnClick()
                    }
                    actionMenus={[]}
              />
        )
    }

    const RenderDataGridTable = () => {

        return (
              <DataGridTable
                    ref={gridRef}
                    refresh={refreshDataGrid}
                    fetchData={fetchData}
                    dataGrid={{
                        idProperty: "id",
                        style: {
                            minHeight: 600
                        },
                        pageSizes: [20, 50, 100],
                        defaultLimit: 10,
                        defaultSkip: 0,
                        checkboxColumn:
                              isPermissionAllow({ functionType: functionTypes.remove.id }),
                        columns,
                        defaultFilterValue: filterValues,
                        onSelectionChange: (items) => {
                            // console.log('items selected : ', items)
                            setSelectedIds(
                                  items
                            )
                        }
                    }}

              />
        )

    }

    return (
          <Box>
              {
                  getHtmlHead({ routeType, title: headTitles.transferRequisitions })
              }

              <CommonBreadcumbs
                    routeType={routeType}
                    pageTitle={t("transferRequisitions")}
                    breadcrumbs={[
                        {
                            displayText: t("transferRequisitions"),
                            props: {
                                // underline: "hover",
                                // href: "/"
                            }
                        }
                    ]}
              />

              {
                  isPermissionAllow({ functionType: functionTypes.pageList.id }) ?
                        <>
                            {
                                RenderToolbar()
                            }
                            {
                                RenderDataGridTable()
                            }
                        </>
                        :
                        (
                              <CommonRestricted/>
                        )
              }

          </Box>
    )
}
